import { ApplicationSettings } from '../settings';
import BaseComponentModel from './base-component-model';
import IImage from '../../models/viewModels/base/IImage';
import IVideo from '../../models/viewModels/base/IVideo';
import { Entry } from 'contentful';
import IShouldReturnEntity from '../../api-clients/IShouldReturnEntity';
import { ExcludedEntitiesHelper } from '../../api-clients/page-template-clients/helpers/excluded-entities-helper';

export default class CarouselComponentModel extends BaseComponentModel {
  public excludedEntitiesHelper = new ExcludedEntitiesHelper();
  public component = 'Carousel';
  carousel: Array<IImage | IVideo>;
  enableBorder: boolean;
  aspectRatio: '16:9' | '1:1' | '4:3' | '3:2';
  imageSize: 'contain' | 'cover' = 'cover';
  id?: string;

  protected shouldReturnEntity(entry: Entry<any>): IShouldReturnEntity {
    const requiredFields = ['carouselItems'];
    const notEmptyFields = ['carouselItems'];

    return this.excludedEntitiesHelper.shouldComponentBeReturned(
      this.component,
      entry,
      requiredFields,
      notEmptyFields,
    );
  }

  protected shouldReturnChildEntities(entry?: Entry<any>): IShouldReturnEntity {
    if (!entry?.sys) {
      return {
        shouldReturn: false,
        shouldNotReturnReasons: [
          {
            entity: this.component,
            entityType: 'component',
            pageUrl: '',
            field: 'sys',
            reason: 'undefined',
          },
        ],
      };
    }

    const contentType = entry?.sys?.contentType?.sys?.id;

    if (contentType === `componentVideo`) {
      const requiredFields = ['fields', 'fields.video'];
      const notEmptyFields = ['fields.video'];

      return this.excludedEntitiesHelper.shouldComponentBeReturned(
        this.component,
        entry,
        requiredFields,
        notEmptyFields,
      );
    }

    if (contentType === `componentImage`) {
      const requiredFields = ['fields', 'fields.image'];
      const notEmptyFields = ['fields.image'];

      return this.excludedEntitiesHelper.shouldComponentBeReturned(
        this.component,
        entry,
        requiredFields,
        notEmptyFields,
      );
    }

    return {
      shouldReturn: false,
      shouldNotReturnReasons: [
        {
          entity: this.component,
          entityType: 'component',
          pageUrl: '',
          field: 'contentType',
          reason: 'undefined',
        },
      ],
    };
  }

  constructor(data: any, appSettings: ApplicationSettings) {
    super('carousel', appSettings);

    const carouselShouldReturn = this.shouldReturnEntity(data);

    if (!carouselShouldReturn.shouldReturn) {
      this.exclusionReasons.push(...carouselShouldReturn.shouldNotReturnReasons);
      this.isValid = false;
      return;
    }

    const allCaroselItems = data.carouselItems;

    const includedCaroselSets = allCaroselItems.filter(
      (a) => this.shouldReturnChildEntities(a).shouldReturn,
    );
    const excludedSets = allCaroselItems.filter(
      (a) => !this.shouldReturnChildEntities(a).shouldReturn,
    );
    excludedSets.forEach((a) => {
      this.exclusionReasons.push(...this.shouldReturnChildEntities(a).shouldNotReturnReasons);
    });

    this.enableBorder = data?.enableBorder;
    this.carousel = includedCaroselSets?.map((el) => {
      const contentType = el?.sys?.contentType?.sys?.id;
      const fields = el?.fields;
      if (contentType === `componentVideo` && el?.fields?.video?.length) {
        return {
          id: el?.sys?.id,
          secureUrl: fields?.video[0]?.secure_url,
          description: fields?.title,
          caption: fields?.video[0]?.context?.custom?.caption || fields?.caption,
          resourceType: 'video',
          previewImageSecureURL: fields?.previewImage?.fields?.image
            ? fields?.previewImage?.fields?.image[0]?.secure_url
            : null,
          previewImageAlt: fields?.previewImage?.fields?.altText,
          title: fields?.title ?? null,
        } as IVideo;
      } else if (contentType === `componentImage` && fields?.image?.length) {
        return {
          id: el?.sys?.id,
          secureUrl: fields?.image[0]?.secure_url,
          caption: fields?.caption || fields?.image[0].context?.custom?.caption,
          alt: fields?.altText,
          format: `f_jpg`,
          resourceType: `image`,
          title: fields?.title ?? null,
        } as IImage;
      }
      return null;
    });

    this.id = data?.id;
    this.aspectRatio = data?.aspectRatio || '16:9';
    this.imageSize = data?.imageSize || 'cover';
  }
}
